import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { X, Send, MessageCircle, User, Bot, Maximize2, Minimize2 } from 'lucide-react'
import { chatAPI, getSessionId } from '../lib/api'
import { API_CONFIG } from '../config/api'

interface Message {
  id: string
  content: string
  sender: 'user' | 'ai'
  timestamp: Date
}

interface AIChatWidgetProps {
  isOpen: boolean
  onToggle: () => void
}

const AIChatWidget: React.FC<AIChatWidgetProps> = ({ isOpen, onToggle }) => {
  const [isMinimized, setIsMinimized] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hi! I'm Rishi Malhotra, a Software Project Manager & Operations Head with 15+ years of experience. I'm here to answer your questions about my background, expertise, projects, or anything else you'd like to know. How can I help you today?",
      sender: 'ai',
      timestamp: new Date()
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [isSendingTranscript, setIsSendingTranscript] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Function to send chat transcript
  const sendTranscript = useCallback(async () => {
    // Only send if there are actual conversation messages (not just welcome message)
    const conversationMessages = messages.filter(msg => 
      msg.id !== '1' || msg.sender === 'user'
    )
    
    if (conversationMessages.length <= 1) {
      console.log('sendTranscript: No conversation to send transcript for')
      return // No real conversation to send
    }
    
    // Prevent multiple simultaneous transcript sends
    if (isSendingTranscript) {
      console.log('sendTranscript: Already sending transcript, skipping')
      return
    }
    
    console.log(`sendTranscript: Starting transcript send for ${conversationMessages.length} messages`)
    setIsSendingTranscript(true)
    
    try {
      const response = await chatAPI.endChat()
      
      if (response.ok) {
        const responseData = await response.json()
        console.log('sendTranscript: Success! Response:', responseData)
        
        // Check if the transcript was actually sent successfully
        if (responseData.success === true) {
          console.log('sendTranscript: Transcript sent successfully, showing notification')
          // Optionally show a subtle notification
          const notification = document.createElement('div')
          notification.textContent = 'Chat transcript sent to Rishi'
          notification.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm'
          document.body.appendChild(notification)
          
          // Remove notification after 3 seconds
          setTimeout(() => {
            if (notification.parentNode) {
              notification.parentNode.removeChild(notification)
            }
          }, 3000)
          
          // Only reset the flag on actual success
          setIsSendingTranscript(false)
        } else {
          console.log('sendTranscript: Backend returned success: false, keeping flag set to prevent retries')
          // Don't reset the flag if backend says it failed
          // This prevents infinite retries
        }
      } else {
        console.error('sendTranscript: Failed to send transcript:', response.status)
        // Reset flag on HTTP error
        setIsSendingTranscript(false)
      }
    } catch (error) {
      console.error('Failed to send transcript automatically:', error)
      // Reset flag on network error
      setIsSendingTranscript(false)
    }
  }, [messages, isSendingTranscript])

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
      // On mobile, always show full screen (no minimize)
      if (window.innerWidth <= 768 && isMinimized) {
        setIsMinimized(false)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [isMinimized])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen, isMinimized])

  // Load chat history when component mounts
  useEffect(() => {
    if (isOpen) {
      loadChatHistory()
    }
  }, [isOpen])

  // Send transcript when chat is closed
  useEffect(() => {
    if (!isOpen && messages.length > 1 && !isSendingTranscript) {
      // Small delay to ensure the close animation completes
      const timer = setTimeout(() => {
        sendTranscript()
      }, 500)
      
      return () => clearTimeout(timer)
    }
  }, [isOpen, messages, sendTranscript, isSendingTranscript])

  // Send transcript when page is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (messages.length > 1) {
        console.log('Page unloading, sending transcript via sendBeacon')
        // Use sendBeacon for more reliable delivery during page unload
        // For cross-domain, we need to include session_id
        const data: { action: string; session_id?: string } = { action: 'send_transcript' }
        if (typeof window !== 'undefined') {
          const currentDomain = window.location.hostname
          const backendDomain = new URL(API_CONFIG.BASE_URL).hostname
          if (currentDomain !== backendDomain) {
            // Cross-domain - include session_id if available
            const sessionId = getSessionId()
            if (sessionId) {
              data.session_id = sessionId
            }
          }
        }
        const success = navigator.sendBeacon(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CHAT_END}`, JSON.stringify(data))
        console.log('sendBeacon result:', success)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && messages.length > 1 && !isSendingTranscript) {
        console.log('Page hidden, sending transcript')
        sendTranscript()
      }
    }

    const handlePageHide = () => {
      if (messages.length > 1 && !isSendingTranscript) {
        console.log('Page hiding, sending transcript')
        sendTranscript()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [messages, sendTranscript, isSendingTranscript])

  const loadChatHistory = async () => {
    try {
      setIsLoadingHistory(true)
      const response = await chatAPI.getHistory()

      if (response.ok) {
        const data = await response.json()
        if (data.messages && data.messages.length > 0) {
          // Convert the loaded messages to our format
          const loadedMessages = data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.content,
            sender: msg.sender,
            timestamp: new Date(msg.timestamp)
          }))
          setMessages(loadedMessages)
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error)
      // Keep the default welcome message if history loading fails
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim(),
      sender: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    try {
      const response = await chatAPI.sendMessage(userMessage.content)

      if (response.ok) {
        const data = await response.json()
        const aiMessage: Message = {
          id: data.message_id || (Date.now() + 1).toString(),
          content: data.response,
          sender: 'ai',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, aiMessage])
      } else {
        // Fallback response if API fails
        const fallbackMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to reach out to me directly at rishimalhotra@me.com.",
          sender: 'ai',
          timestamp: new Date()
        }
        setMessages(prev => [...prev, fallbackMessage])
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      // Fallback response on network error
      const fallbackMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: "I apologize, but I'm experiencing a connection issue. Please try again later or contact me directly at rishimalhotra@me.com.",
        sender: 'ai',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, fallbackMessage])
    } finally {
      setIsTyping(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const clearChat = async () => {
    try {
      await chatAPI.clearChat()
      
      // Reset to welcome message
      setMessages([
        {
          id: '1',
          content: "Hi! I'm Rishi Malhotra, a Software Project Manager & Operations Head with 15+ years of experience. I'm here to answer your questions about my background, expertise, projects, or anything else you'd like to know. How can I help you today?",
          sender: 'ai',
          timestamp: new Date()
        }
      ])
    } catch (error) {
      console.error('Failed to clear chat:', error)
    }
  }

  // Comprehensive markdown parser for chat messages
  const parseMarkdown = (text: string): string => {
    // First escape HTML to prevent XSS
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')

    // Headers (must come before other formatting)
    html = html.replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold mt-3 mb-2">$1</h3>')
    html = html.replace(/^## (.*$)/gm, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
    html = html.replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')

    // Code blocks (must come before inline code) - remove newlines from content
    html = html.replace(/```(\w+)?\n?([\s\S]*?)```/g, (match, lang, content) => {
      // Clean up newlines in code content to prevent double conversion
      const cleanContent = content.replace(/\n/g, ' ').trim()
      return `<pre class="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg my-2 overflow-x-auto"><code class="text-sm font-mono">${cleanContent}</code></pre>`
    })
    
    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>')

    // Bold and italic (order matters)
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>')
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/\*([^*\s][^*]*[^*\s]|\S)\*/g, '<em>$1</em>')

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-cyber-600 dark:text-cyber-400 hover:underline">$1</a>')

    // Strikethrough
    html = html.replace(/~~(.*?)~~/g, '<del>$1</del>')

    // Blockquotes
    html = html.replace(/^> (.*)$/gm, '<blockquote class="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 my-2 italic">$1</blockquote>')

    // Horizontal rules
    html = html.replace(/^---$/gm, '<hr class="my-4 border-gray-300 dark:border-gray-600">')

    // Lists (unordered)
    html = html.replace(/^\* (.*)$/gm, '<li class="unordered-item">$1</li>')
    html = html.replace(/^- (.*)$/gm, '<li class="unordered-item">$1</li>')
    html = html.replace(/^\+ (.*)$/gm, '<li class="unordered-item">$1</li>')

    // Lists (ordered) - preserve original numbering
    html = html.replace(/^\d+\. (.*)$/gm, '<li class="ordered-item">$1</li>')

    // Wrap consecutive list items in proper ul/ol tags
    html = html.replace(/(<li class="unordered-item">.*<\/li>(\s*<li class="unordered-item">.*<\/li>)*)/g, '<ul class="my-2 space-y-1 list-disc list-inside">$1</ul>')
    html = html.replace(/(<li class="ordered-item">.*<\/li>(\s*<li class="ordered-item">.*<\/li>)*)/g, '<ol class="my-2 space-y-1 list-decimal list-inside">$1</ol>')

    // Clean up the list item classes after wrapping
    html = html.replace(/class="unordered-item"/g, 'class="ml-2"')
    html = html.replace(/class="ordered-item"/g, 'class="ml-2"')

    // Line breaks (convert double newlines to <br> for paragraph separation)
    html = html.replace(/\n\n/g, '<br>')
    
    // Remove the custom BR styling since we're using fewer BR tags now

    return html
  }

  // Function to safely render HTML content with full markdown support
  const renderMessageContent = (content: string) => {
    return { __html: parseMarkdown(content) }
  }

  if (!isOpen) {
    return (
      <motion.button
        onClick={onToggle}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-cyber-600 to-cyber-700 text-white rounded-full shadow-2xl hover:shadow-cyber-500/25 transition-all duration-300 hover:scale-110 z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <MessageCircle className="w-8 h-8 mx-auto" />
      </motion.button>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 100 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 100 }}
      className={`fixed z-50 transition-all duration-500 ease-in-out ${
        isMinimized 
          ? 'bottom-6 right-6 w-96 h-[600px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden' 
          : 'bottom-0 right-0 w-full h-full bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden'
      }`}
      style={{
        ...(isMinimized ? {} : { 
          width: '100vw',
          height: '100vh',
          borderRadius: '0'
        })
      }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyber-600 to-cyber-700 text-white p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
            <img 
              src="/rishi-profile.jpg" 
              alt="Rishi Malhotra" 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to bot icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <Bot className="w-5 h-5 hidden" />
          </div>
          <div>
            <h3 className="font-semibold">Chat with Rishi</h3>
            {isSendingTranscript && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Sending transcript...
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={clearChat}
            className="p-1 hover:bg-white/20 rounded transition-colors duration-200 text-xs"
            title="Clear chat"
          >
            Clear
          </button>

          {!isMobile && (
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-white/20 rounded transition-colors duration-200"
              title={isMinimized ? "Expand chat" : "Minimize chat"}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={onToggle}
            className="p-1 hover:bg-white/20 rounded transition-colors duration-200"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages - Always visible, even when minimized */}
      <div className="flex-1 p-4 overflow-y-auto" style={{ height: isMinimized ? 'calc(600px - 140px)' : 'calc(100vh - 140px)' }}>
            {isLoadingHistory ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 dark:text-gray-400">Loading conversation...</div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`${isMinimized ? 'max-w-[95%]' : 'max-w-[80%]'} p-3 rounded-lg ${
                        message.sender === 'user'
                          ? 'bg-cyber-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}
                    >
                      <div className="flex items-start space-x-2">
                        {message.sender === 'ai' && (
                          <img 
                            src="/rishi-profile.jpg" 
                            alt="Rishi Malhotra" 
                            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              // Fallback to bot icon if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        )}
                        {message.sender === 'ai' && (
                          <Bot className="w-6 h-6 text-cyber-600 dark:text-cyber-400 flex-shrink-0 hidden" />
                        )}
                        <div className="flex-1">
                          <div 
                            className="text-sm leading-relaxed text-left prose prose-sm max-w-none dark:prose-invert"
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word'
                            }}
                            dangerouslySetInnerHTML={renderMessageContent(message.content)}
                          />


                          <p className="text-xs opacity-70 mt-1 text-left">
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        {message.sender === 'user' && (
                          <User className="w-4 h-4 mt-1 text-white/70 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <img 
                          src="/rishi-profile.jpg" 
                          alt="Rishi Malhotra" 
                          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                        <Bot className="w-6 h-6 text-cyber-600 dark:text-cyber-400 hidden" />
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex space-x-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                disabled={isTyping}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="px-4 py-2 bg-cyber-600 hover:bg-cyber-700 disabled:bg-gray-400 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
    </motion.div>
  )
}

export default AIChatWidget
